import React from "react";
import { graphql, Link } from "gatsby";
import Layout from "../components/layout";
import "../styles/pageTemplate-styles.css";
import "../styles/career-resources-styles.css";
import "../styles/careers-in.css";
import { Container, Col, Row } from "react-bootstrap";
import { renderRichText } from "gatsby-source-contentful/rich-text";
import Seo from "../components/seo";
import { MARKS, INLINES, BLOCKS } from "@contentful/rich-text-types";
import StatCard from "../components/card";
import ModalVideo from "../components/modalVideo";
import Slideshow from "../components/carousel";
import FAQ from "../components/faq";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { MenuTrigger } from "@contentful/f36-components";
import ScrollToTopButton from "../components/scrollToTopButton";

const pageTemplate = ({ data, pageContext }) => {
  const { nodes: pages } = data.allContentfulCrPage;
  const { nodes: crContents } = data.allContentfulCrContent;
  const { navSlug } = pageContext;

  // *********************************** IF THE LINKS DO NOT LOAD ON THE FIRST CLICK REVISE THIS SECTION ***********************************
  function getEntryWithId(pageId) {
    // ** First match id with the page

    // Find the parent node based on the parent ID of the matching node
    const matchingIdCrPage = data.allContentfulCrPage.nodes.find(
      (node) => node.contentful_id === pageId
    );
    const matchingIdPdf = data.allContentfulPdf.nodes.find(
      (node) => node.contentful_id === pageId
    );
    const matchingId = matchingIdPdf || matchingIdCrPage; // Returns the first truthy value (i.e., the first match found)

    // ** Get the slug from the matched page
    const matchingEntry = matchingId.slug;
    // ** Get the typename from the matched page
    const matchingType = matchingId.__typename;
    // ** If there is no matching entry gtfo brah
    if (!matchingEntry) return;

    // Recursive function to find the matching slug and build the URL path

    const buildUrlPath = (items, slug, type) => {
      // Iterate over each node in the current level
      for (const item of items) {
        if (type === "ContentfulPdf") {
          // Found the matching slug and type, construct URL path with .pdf extension
          return `${slug}.pdf`;
        }

        // Check if the current node matches the slug
        if (item.navSlug === slug) {
          // Found the matching slug, construct URL path without .pdf extension
          return `${item.navSlug}`;
        }

        // Check if the current node has navNextLevel
        if (item.navNextLevel) {
          // Recursively search in the navNextLevel

          const nextLevelPath = buildUrlPath(item.navNextLevel, slug);

          // If the matching slug is found in the next level, prepend current slug to the path
          if (nextLevelPath !== "") {
            return `${item.navSlug}/${nextLevelPath}`;
          }
        }
      }

      // If matching slug not found in current nodes or their children, return empty string
      return "";
    };

    const urlPath = buildUrlPath(
      data.allContentfulCrNavGroup.nodes,
      matchingEntry,
      matchingType
    );
    return `/${urlPath}`;
  }

  const options = {
    renderText: (text) => {
      return text.split("\n").reduce((children, textSegment, index) => {
        return [...children, index > 0 && <br key={index} />, textSegment];
      }, []);
    },
    renderMark: {
      [MARKS.BOLD]: (text) => <strong className="font-bold">{text}</strong>,
    },
    renderNode: {
      [INLINES.ENTRY_HYPERLINK]: (node, children) => {
        const referencedEntry = getEntryWithId(node.data.target.sys.id);
        // Check if it's a pdf
        const isPdf = referencedEntry.endsWith(".pdf");
        return isPdf ? (
          <a href={`${referencedEntry}`} target="_blank">
            {children}
          </a>
        ) : (
          <Link to={`${referencedEntry}`}>{children}</Link>
        );
      },
      [INLINES.HYPERLINK]: (node, children) => {
        const referencedEntry = node.data.uri;
        return (
          <Link to={`${referencedEntry}`} target="_blank">
            {children}
          </Link>
        );
      },
      [INLINES.EMBEDDED_ENTRY]: (node, children) => {
        const thisSlug = node.data.target.sys.slug;
        return (
          <>
            <a href={thisSlug} className="underline">
              {children}
            </a>
          </>
        );
      },
      [BLOCKS.HEADING_2]: (node, children) => {
        return <h2>{children}</h2>;
      },
      [BLOCKS.TABLE]: (node, children) => {
        const getPathname = () => {
          if (typeof window !== "undefined") {
            return window.location.pathname;
          }
          return ""; // Default path or server-side equivalent
        };

        const path = getPathname();

        return <table className="other-table">{children}</table>;
      },
    },
  };

  // ***************************** IF NULL - conditions *****************************
  if (
    !pages ||
    !Array.isArray(pages) ||
    !crContents ||
    !Array.isArray(crContents)
  )
    return null;

  // ***************************** currentPage checks to see if the slug from CR PAGE and the navSlug from the CrNavGroup MATCH *****************************
  const currentPage = pages.find((conts) => conts.slug === navSlug);

  if (!currentPage) return null;

  return (
    <Layout>
      <Container fluid key={currentPage.slug} className={currentPage.slug}>
        <ScrollToTopButton />
        {currentPage.content.map((gridItem) => {
          if (gridItem.layout === "1 Column") {
            return (
              <>
                <Container
                  fluid
                  className={gridItem.name
                    .toLowerCase()
                    .replace(/[\s:\/,&+]+/g, "-")}
                >
                  <Container>
                    {gridItem.showTitle ? <h2>{gridItem.name}</h2> : null}
                    <Row
                      className={currentPage.title
                        .toLowerCase()
                        .replace(/[\s:,]+/g, "-")}
                    >
                      <Col xs={12} key={gridItem.id}>
                        {/* ***************************** 1 COLUMN - SINGLE COLUMN ***************************** */}
                        {Array.isArray(gridItem.singleColumn) &&
                          gridItem.singleColumn.map((conSing) => (
                            <div id={conSing.styleId}>
                              {conSing.showName ? (
                                <h2>{conSing.name}</h2>
                              ) : null}
                              {conSing.body ? (
                                <p>{renderRichText(conSing.body, options)}</p>
                              ) : null}
                              {conSing.image ? (
                                <img
                                  loading="lazy"
                                  alt={conSing.name}
                                  src={conSing.image.url}
                                />
                              ) : null}
                              {conSing.caption ? (
                                <caption>{conSing.caption}</caption>
                              ) : conSing.captionRichText ? (
                                <caption>
                                  {renderRichText(
                                    conSing.captionRichText,
                                    options
                                  )}
                                </caption>
                              ) : null}
                              {/* ********************************************** PDF DOCUMENT ********************************************** */}
                              {conSing.documentTitle ? (
                                <div>
                                  <h4>
                                    <Link to={`/${conSing.slug}.pdf`}>
                                      {conSing.documentTitle}{" "}
                                      <FontAwesomeIcon icon={faDownload} />{" "}
                                    </Link>
                                  </h4>
                                  {conSing.pdfDocument !== null ? (
                                    <p>{conSing.pdfDocument.description}</p>
                                  ) : null}
                                </div>
                              ) : null}

                              {/* ********************************************** STATCARD ********************************************** */}

                              {conSing.cardTitle ? (
                                <StatCard
                                  key={conSing.id}
                                  // styleId={conSing.styleId}
                                  title={conSing.cardTitle}
                                  subtitle={conSing.number}
                                  url={conSing.url}
                                  description={
                                    conSing.description &&
                                    conSing.description.description ? (
                                      <span>
                                        {conSing.description.description}
                                      </span>
                                    ) : null
                                  }
                                  {...(conSing.url ? { url: conSing.url } : {})}
                                  titleClass={conSing.cardTitle
                                    .toLowerCase()
                                    .replace(/[\s:,]+/g, "-")}
                                />
                              ) : null}
                              {conSing.questionsAndAnswers ? (
                                <FAQ
                                  content={conSing.questionsAndAnswers}
                                  data={data}
                                />
                              ) : null}

                              {/* ********************************************** VIDEO ********************************************** */}
                              {conSing.videoName ? (
                                <ModalVideo
                                  key={conSing.id}
                                  title={conSing.videoName}
                                  youtubeID={conSing.videoId}
                                  caption={conSing.videoCaption}
                                  videoImageThumbnail={
                                    conSing.videoImageThumbnail
                                  }
                                />
                              ) : null}
                              {/* ********************************************** CAROUSEL ********************************************** */}
                              {conSing.carouselSlideContainer ? (
                                <Slideshow
                                  content={conSing.carouselSlideContainer}
                                />
                              ) : null}
                            </div>
                          ))}
                      </Col>
                    </Row>
                  </Container>
                </Container>
              </>
            );
          } else if (gridItem.layout === "2 Column - 75/25") {
            return (
              <>
                <Container
                  fluid
                  className={gridItem.name
                    .toLowerCase()
                    .replace(/[\s:\/,&+]+/g, "-")}
                >
                  <Container>
                    {gridItem.showTitle ? <h2>{gridItem.name}</h2> : null}
                    <Row>
                      <Col xs={12} md={9} key={gridItem.id}>
                        {/* ***************************** 2 COLUMN - 75/25 - LEFT COLUMN ***************************** */}
                        {Array.isArray(gridItem.leftColumn) &&
                          gridItem.leftColumn.map((conLeft) => (
                            <div
                              className={
                                conLeft.name
                                  ? conLeft.name
                                      .toLowerCase()
                                      .replace(/[\s:,]+/g, "-")
                                  : null
                              }
                              id={conLeft.styleId ? conLeft.styleId : null}
                            >
                              {conLeft.showName ? (
                                <h2>{conLeft.name}</h2>
                              ) : null}
                              {conLeft.body ? (
                                <p>{renderRichText(conLeft.body, options)}</p>
                              ) : null}
                              {conLeft.image ? (
                                <img
                                  loading="lazy"
                                  alt={conLeft.name}
                                  src={conLeft.image.url}
                                />
                              ) : null}
                              {conLeft.caption ? (
                                <caption>{conLeft.caption}</caption>
                              ) : conLeft.captionRichText ? (
                                <caption>
                                  {renderRichText(
                                    conLeft.captionRichText,
                                    options
                                  )}
                                </caption>
                              ) : null}
                              {/* ********************************************** STATCARD ********************************************** */}
                              {conLeft.cardTitle ? (
                                <StatCard
                                  key={conLeft.id}
                                  icon={
                                    conLeft.icon ? (
                                      <img
                                        loading="lazy"
                                        alt={conLeft.cardTitle}
                                        src={conLeft.icon.url}
                                        className="card-icons"
                                      />
                                    ) : null
                                  }
                                  title={conLeft.cardTitle}
                                  subtitle={conLeft.number}
                                  url={conLeft.url}
                                  description={
                                    conLeft.description &&
                                    conLeft.description.description ? (
                                      <span>
                                        {conLeft.description.description}
                                      </span>
                                    ) : null
                                  }
                                  {...(conLeft.url ? { url: conLeft.url } : {})}
                                  titleClass={conLeft.cardTitle
                                    .toLowerCase()
                                    .replace(/[\s:,]+/g, "-")}
                                />
                              ) : null}
                              {conLeft.questionsAndAnswers ? (
                                <FAQ
                                  content={conLeft.questionsAndAnswers}
                                  data={data}
                                />
                              ) : null}
                              {/* ********************************************** VIDEO ********************************************** */}
                              {conLeft.videoName ? (
                                <ModalVideo
                                  key={conLeft.id}
                                  title={conLeft.videoName}
                                  youtubeID={conLeft.videoId}
                                  caption={conLeft.videoCaption}
                                  videoImageThumbnail={
                                    conLeft.videoImageThumbnail
                                  }
                                />
                              ) : null}
                            </div>
                          ))}
                      </Col>
                      <Col xs={12} md={3} key={gridItem.id}>
                        {/* ***************************** 2 COLUMN - 75/25 - RIGHT COLUMN ***************************** */}
                        {Array.isArray(gridItem.rightColumn) &&
                          gridItem.rightColumn.map((conRight) => (
                            <div
                              className={
                                conRight.name
                                  ? conRight.name
                                      .toLowerCase()
                                      .replace(/[\s:,]+/g, "-")
                                  : null
                              }
                              id={conRight.styleId ? conRight.styleId : null}
                            >
                              {conRight.showName ? (
                                <h2>{conRight.name}</h2>
                              ) : null}
                              {conRight.body ? (
                                <p>{renderRichText(conRight.body, options)}</p>
                              ) : null}
                              {conRight.image ? (
                                <img
                                  loading="lazy"
                                  alt={conRight.name}
                                  src={conRight.image.url}
                                />
                              ) : null}
                              {conRight.caption ? (
                                <caption>{conRight.caption}</caption>
                              ) : conRight.captionRichText ? (
                                <caption>
                                  {renderRichText(
                                    conRight.captionRichText,
                                    options
                                  )}
                                </caption>
                              ) : null}
                              {/* ********************************************** STATCARD ********************************************** */}
                              {conRight.cardTitle ? (
                                <StatCard
                                  key={conRight.id}
                                  icon={
                                    conRight.icon ? (
                                      <img
                                        loading="lazy"
                                        alt={conRight.cardTitle}
                                        src={conRight.icon.url}
                                        className="card-icons"
                                      />
                                    ) : null
                                  }
                                  title={conRight.cardTitle}
                                  subtitle={conRight.number}
                                  url={conRight.url}
                                  description={
                                    conRight.description &&
                                    conRight.description.description ? (
                                      <span>
                                        {conRight.description.description}
                                      </span>
                                    ) : null
                                  }
                                  {...(conRight.url
                                    ? { url: conRight.url }
                                    : {})}
                                  titleClass={conRight.cardTitle
                                    .toLowerCase()
                                    .replace(/[\s:,]+/g, "-")}
                                />
                              ) : null}
                              {conRight.questionsAndAnswers ? (
                                <FAQ
                                  content={conRight.questionsAndAnswers}
                                  data={data}
                                />
                              ) : null}
                              {/* ********************************************** VIDEO ********************************************** */}
                              {conRight.videoName ? (
                                <ModalVideo
                                  key={conRight.id}
                                  title={conRight.videoName}
                                  youtubeID={conRight.videoId}
                                  caption={conRight.videoCaption}
                                  videoImageThumbnail={
                                    conRight.videoImageThumbnail
                                  }
                                />
                              ) : null}
                            </div>
                          ))}
                      </Col>
                    </Row>
                  </Container>
                </Container>
              </>
            );
          } else if (gridItem.layout === "2 Column - 25/75") {
            return (
              <>
                {" "}
                <Container
                  fluid
                  className={gridItem.name
                    .toLowerCase()
                    .replace(/[\s:\/,&+]+/g, "-")}
                >
                  <Container>
                    {gridItem.showTitle ? <h2>{gridItem.name}</h2> : null}
                    <Row>
                      <Col xs={12} md={3} key={gridItem.id}>
                        {/* ***************************** 2 COLUMN - 25/75 - LEFT COLUMN ***************************** */}
                        {Array.isArray(gridItem.leftColumn) &&
                          gridItem.leftColumn.map((conLeft) => (
                            <div
                              className={
                                conLeft.name
                                  ? conLeft.name
                                      .toLowerCase()
                                      .replace(/[\s:,]+/g, "-")
                                  : null
                              }
                              id={conLeft.styleId ? conLeft.styleId : null}
                            >
                              {conLeft.showName ? (
                                <h2>{conLeft.name}</h2>
                              ) : null}
                              {conLeft.body ? (
                                <p>{renderRichText(conLeft.body, options)}</p>
                              ) : null}

                              {conLeft.image ? (
                                <img
                                  loading="lazy"
                                  alt={conLeft.name}
                                  src={conLeft.image.url}
                                />
                              ) : null}
                              {conLeft.caption ? (
                                <caption>{conLeft.caption}</caption>
                              ) : conLeft.captionRichText ? (
                                <caption>
                                  {renderRichText(
                                    conLeft.captionRichText,
                                    options
                                  )}
                                </caption>
                              ) : null}

                              {/* ********************************************** STATCARD ********************************************** */}
                              {conLeft.cardTitle ? (
                                <StatCard
                                  key={conLeft.id}
                                  icon={
                                    conLeft.icon ? (
                                      <img
                                        loading="lazy"
                                        alt={conLeft.cardTitle}
                                        src={conLeft.icon.url}
                                        className="card-icons"
                                      />
                                    ) : null
                                  }
                                  url={conLeft.url}
                                  title={conLeft.cardTitle}
                                  subtitle={conLeft.number}
                                  description={
                                    conLeft.description &&
                                    conLeft.description.description ? (
                                      <span>
                                        {conLeft.description.description}
                                      </span>
                                    ) : null
                                  }
                                  {...(conLeft.url ? { url: conLeft.url } : {})}
                                  titleClass={conLeft.cardTitle
                                    .toLowerCase()
                                    .replace(/[\s:,]+/g, "-")}
                                />
                              ) : null}
                              {conLeft.questionsAndAnswers ? (
                                <FAQ
                                  content={conLeft.questionsAndAnswers}
                                  data={data}
                                />
                              ) : null}
                              {/* ********************************************** VIDEO ********************************************** */}
                              {conLeft.videoName ? (
                                <ModalVideo
                                  key={conLeft.id}
                                  title={conLeft.videoName}
                                  youtubeID={conLeft.videoId}
                                  caption={conLeft.videoCaption}
                                  videoImageThumbnail={
                                    conLeft.videoImageThumbnail
                                  }
                                />
                              ) : null}
                            </div>
                          ))}
                      </Col>
                      <Col xs={12} md={9} key={gridItem.id}>
                        {/* ***************************** 2 COLUMN - 25/75 - RIGHT COLUMN ***************************** */}
                        {Array.isArray(gridItem.rightColumn) &&
                          gridItem.rightColumn.map((conRight) => (
                            <div
                              className={
                                conRight.name
                                  ? conRight.name
                                      .toLowerCase()
                                      .replace(/[\s:,]+/g, "-")
                                  : null
                              }
                              id={conRight.styleId ? conRight.styleId : null}
                            >
                              {conRight.showName ? (
                                <h2>{conRight.name}</h2>
                              ) : null}
                              {conRight.body ? (
                                <p>{renderRichText(conRight.body, options)}</p>
                              ) : null}
                              {conRight.image ? (
                                <img
                                  loading="lazy"
                                  alt={conRight.name}
                                  src={conRight.image.url}
                                />
                              ) : null}
                              {conRight.caption ? (
                                <caption>{conRight.caption}</caption>
                              ) : conRight.captionRichText ? (
                                <caption>
                                  {renderRichText(
                                    conRight.captionRichText,
                                    options
                                  )}
                                </caption>
                              ) : null}
                              {/* ********************************************** STATCARD ********************************************** */}
                              {conRight.cardTitle ? (
                                <StatCard
                                  key={conRight.id}
                                  icon={
                                    conRight.icon ? (
                                      <img
                                        loading="lazy"
                                        alt={conRight.cardTitle}
                                        src={conRight.icon.url}
                                        className="card-icons"
                                      />
                                    ) : null
                                  }
                                  url={conRight.url}
                                  title={conRight.cardTitle}
                                  subtitle={conRight.number}
                                  description={
                                    conRight.description &&
                                    conRight.description.description ? (
                                      <span>
                                        {conRight.description.description}
                                      </span>
                                    ) : null
                                  }
                                  {...(conRight.url
                                    ? { url: conRight.url }
                                    : {})}
                                  titleClass={conRight.cardTitle
                                    .toLowerCase()
                                    .replace(/[\s:,]+/g, "-")}
                                />
                              ) : null}
                              {conRight.questionsAndAnswers ? (
                                <FAQ
                                  content={conRight.questionsAndAnswers}
                                  data={data}
                                />
                              ) : null}
                              {/* ********************************************** VIDEO ********************************************** */}
                              {conRight.videoName ? (
                                <ModalVideo
                                  key={conRight.id}
                                  title={conRight.videoName}
                                  youtubeID={conRight.videoId}
                                  caption={conRight.videoCaption}
                                  videoImageThumbnail={
                                    conRight.videoImageThumbnail
                                  }
                                />
                              ) : null}
                            </div>
                          ))}
                      </Col>
                    </Row>
                  </Container>
                </Container>
              </>
            );
          } else if (gridItem.layout === "2 Column - 33/66") {
            return (
              <>
                <Container
                  fluid
                  className={gridItem.name
                    .toLowerCase()
                    .replace(/[\s:\/,&+]+/g, "-")}
                >
                  <Container>
                    {gridItem.showTitle ? <h2>{gridItem.name}</h2> : null}
                    <Row>
                      <Col xs={12} md={4} key={gridItem.id}>
                        {/* ***************************** 2 COLUMN - 33/66 - LEFT COLUMN ***************************** */}
                        {Array.isArray(gridItem.leftColumn) &&
                          gridItem.leftColumn.map((conLeft) => (
                            <div
                              className={
                                conLeft.name
                                  ? conLeft.name
                                      .toLowerCase()
                                      .replace(/[\s:,]+/g, "-")
                                  : null
                              }
                              id={conLeft.styleId ? conLeft.styleId : null}
                            >
                              {conLeft.showName ? (
                                <h2>{conLeft.name}</h2>
                              ) : null}
                              {conLeft.body ? (
                                <p>{renderRichText(conLeft.body, options)}</p>
                              ) : null}
                              {conLeft.image ? (
                                <img
                                  loading="lazy"
                                  alt={conLeft.name}
                                  src={conLeft.image.url}
                                />
                              ) : null}
                              {conLeft.caption ? (
                                <caption>{conLeft.caption}</caption>
                              ) : conLeft.captionRichText ? (
                                <caption>
                                  {renderRichText(
                                    conLeft.captionRichText,
                                    options
                                  )}
                                </caption>
                              ) : null}
                              {/* ********************************************** STATCARD ********************************************** */}
                              {conLeft.cardTitle ? (
                                <StatCard
                                  key={conLeft.id}
                                  icon={
                                    conLeft.icon ? (
                                      <img
                                        loading="lazy"
                                        alt={conLeft.cardTitle}
                                        src={conLeft.icon.url}
                                        className="card-icons"
                                      />
                                    ) : null
                                  }
                                  url={conLeft.url}
                                  title={conLeft.cardTitle}
                                  subtitle={conLeft.number}
                                  description={
                                    conLeft.description &&
                                    conLeft.description.description ? (
                                      <span>
                                        {conLeft.description.description}
                                      </span>
                                    ) : null
                                  }
                                  {...(conLeft.url ? { url: conLeft.url } : {})}
                                  titleClass={conLeft.cardTitle
                                    .toLowerCase()
                                    .replace(/[\s:,]+/g, "-")}
                                />
                              ) : null}
                              {conLeft.questionsAndAnswers ? (
                                <FAQ
                                  content={conLeft.questionsAndAnswers}
                                  data={data}
                                />
                              ) : null}
                              {/* ********************************************** VIDEO ********************************************** */}
                              {conLeft.videoName ? (
                                <ModalVideo
                                  key={conLeft.id}
                                  title={conLeft.videoName}
                                  youtubeID={conLeft.videoId}
                                  caption={conLeft.videoCaption}
                                  videoImageThumbnail={
                                    conLeft.videoImageThumbnail
                                  }
                                />
                              ) : null}
                            </div>
                          ))}
                      </Col>
                      <Col xs={12} md={8} key={gridItem.id}>
                        {/* ***************************** 2 COLUMN - 33/66 - RIGHT COLUMN ***************************** */}
                        {Array.isArray(gridItem.rightColumn) &&
                          gridItem.rightColumn.map((conRight) => (
                            <div
                              className={
                                conRight.name
                                  ? conRight.name
                                      .toLowerCase()
                                      .replace(/[\s:,]+/g, "-")
                                  : null
                              }
                              id={conRight.styleId ? conRight.styleId : null}
                            >
                              {conRight.showName ? (
                                <h2>{conRight.name}</h2>
                              ) : null}
                              {conRight.body ? (
                                <p>{renderRichText(conRight.body, options)}</p>
                              ) : null}
                              {conRight.image ? (
                                <img
                                  loading="lazy"
                                  alt={conRight.name}
                                  src={conRight.image.url}
                                />
                              ) : null}
                              {conRight.caption ? (
                                <caption>{conRight.caption}</caption>
                              ) : conRight.captionRichText ? (
                                <caption>
                                  {renderRichText(
                                    conRight.captionRichText,
                                    options
                                  )}
                                </caption>
                              ) : null}
                              {/* ********************************************** STATCARD ********************************************** */}
                              {conRight.cardTitle ? (
                                <StatCard
                                  key={conRight.id}
                                  icon={
                                    conRight.icon ? (
                                      <img
                                        loading="lazy"
                                        alt={conRight.cardTitle}
                                        src={conRight.icon.url}
                                        className="card-icons"
                                      />
                                    ) : null
                                  }
                                  url={conRight.url}
                                  title={conRight.cardTitle}
                                  subtitle={conRight.number}
                                  description={
                                    conRight.description &&
                                    conRight.description.description ? (
                                      <span>
                                        {conRight.description.description}
                                      </span>
                                    ) : null
                                  }
                                  {...(conRight.url
                                    ? { url: conRight.url }
                                    : {})}
                                  titleClass={conRight.cardTitle
                                    .toLowerCase()
                                    .replace(/[\s:,]+/g, "-")}
                                />
                              ) : null}
                              {conRight.questionsAndAnswers ? (
                                <FAQ
                                  content={conRight.questionsAndAnswers}
                                  data={data}
                                />
                              ) : null}
                              {/* ********************************************** VIDEO ********************************************** */}
                              {conRight.videoName ? (
                                <ModalVideo
                                  key={conRight.id}
                                  title={conRight.videoName}
                                  youtubeID={conRight.videoId}
                                  caption={conRight.videoCaption}
                                  videoImageThumbnail={
                                    conRight.videoImageThumbnail
                                  }
                                />
                              ) : null}
                            </div>
                          ))}
                      </Col>
                    </Row>
                  </Container>
                </Container>
              </>
            );
          } else if (gridItem.layout === "2 Column - 66/33") {
            return (
              <>
                <Container
                  fluid
                  className={gridItem.name
                    .toLowerCase()
                    .replace(/[\s:\/,&+]+/g, "-")}
                >
                  <Container>
                    {gridItem.showTitle ? <h2>{gridItem.name}</h2> : null}
                    <Row>
                      <Col xs={12} md={8} key={gridItem.id}>
                        {/* ***************************** 2 COLUMN - 66/33 - LEFT COLUMN ***************************** */}
                        {Array.isArray(gridItem.leftColumn) &&
                          gridItem.leftColumn.map((conLeft) => (
                            <div
                              className={
                                conLeft.name
                                  ? conLeft.name
                                      .toLowerCase()
                                      .replace(/[\s:,]+/g, "-")
                                  : null
                              }
                              id={conLeft.styleId ? conLeft.styleId : null}
                            >
                              {conLeft.showName ? (
                                <h2>{conLeft.name}</h2>
                              ) : null}
                              {conLeft.body ? (
                                <p>{renderRichText(conLeft.body, options)}</p>
                              ) : null}
                              {conLeft.image ? (
                                <img
                                  loading="lazy"
                                  alt={conLeft.name}
                                  src={conLeft.image.url}
                                />
                              ) : null}
                              {conLeft.caption ? (
                                <caption>{conLeft.caption}</caption>
                              ) : conLeft.captionRichText ? (
                                <caption>
                                  {renderRichText(
                                    conLeft.captionRichText,
                                    options
                                  )}
                                </caption>
                              ) : null}
                              {/* ********************************************** PDF DOCUMENT ********************************************** */}
                              {conLeft.documentTitle ? (
                                <div>
                                  <h4>
                                    <Link to={`/${conLeft.slug}.pdf`}>
                                      {conLeft.documentTitle}{" "}
                                      <FontAwesomeIcon icon={faDownload} />{" "}
                                    </Link>
                                  </h4>
                                  {conLeft.pdfDocument !== null ? (
                                    <p>{conLeft.pdfDocument.description}</p>
                                  ) : null}
                                </div>
                              ) : null}
                              {/* ********************************************** VIDEO ********************************************** */}
                              {conLeft.videoName ? (
                                <ModalVideo
                                  key={conLeft.id}
                                  title={conLeft.videoName}
                                  youtubeID={conLeft.videoId}
                                  caption={conLeft.videoCaption}
                                  videoImageThumbnail={
                                    conLeft.videoImageThumbnail
                                  }
                                />
                              ) : null}
                              {/* ********************************************** STATCARD ********************************************** */}
                              {conLeft.cardTitle ? (
                                <StatCard
                                  key={conLeft.id}
                                  icon={
                                    conLeft.icon ? (
                                      <img
                                        loading="lazy"
                                        alt={conLeft.cardTitle}
                                        src={conLeft.icon.url}
                                        className="card-icons"
                                      />
                                    ) : null
                                  }
                                  url={conLeft.url}
                                  title={conLeft.cardTitle}
                                  subtitle={conLeft.number}
                                  description={
                                    conLeft.description &&
                                    conLeft.description.description ? (
                                      <span>
                                        {conLeft.description.description}
                                      </span>
                                    ) : null
                                  }
                                  {...(conLeft.url ? { url: conLeft.url } : {})}
                                  titleClass={conLeft.cardTitle
                                    .toLowerCase()
                                    .replace(/[\s:,]+/g, "-")}
                                />
                              ) : null}
                              {conLeft.questionsAndAnswers ? (
                                <FAQ
                                  content={conLeft.questionsAndAnswers}
                                  data={data}
                                />
                              ) : null}
                            </div>
                          ))}
                      </Col>
                      <Col xs={12} md={4} key={gridItem.id}>
                        {/* ***************************** 2 COLUMN - 66/33 - RIGHT COLUMN ***************************** */}
                        {Array.isArray(gridItem.rightColumn) &&
                          gridItem.rightColumn.map((conRight) => (
                            <div
                              className={
                                conRight.name
                                  ? conRight.name
                                      .toLowerCase()
                                      .replace(/[\s:,]+/g, "-")
                                  : null
                              }
                              id={conRight.styleId ? conRight.styleId : null}
                            >
                              {conRight.showName ? (
                                <h2>{conRight.name}</h2>
                              ) : null}
                              {conRight.body ? (
                                <p>{renderRichText(conRight.body, options)}</p>
                              ) : null}
                              {conRight.image ? (
                                <img
                                  loading="lazy"
                                  alt={conRight.name}
                                  src={conRight.image.url}
                                />
                              ) : null}
                              {conRight.caption ? (
                                <caption>{conRight.caption}</caption>
                              ) : conRight.captionRichText ? (
                                <caption>
                                  {renderRichText(
                                    conRight.captionRichText,
                                    options
                                  )}
                                </caption>
                              ) : null}

                              {/* ********************************************** STATCARD ********************************************** */}
                              {conRight.cardTitle ? (
                                <StatCard
                                  key={conRight.id}
                                  icon={
                                    conRight.icon ? (
                                      <img
                                        loading="lazy"
                                        alt={conRight.cardTitle}
                                        src={conRight.icon.url}
                                        className="card-icons"
                                      />
                                    ) : null
                                  }
                                  title={conRight.cardTitle}
                                  url={conRight.url}
                                  subtitle={conRight.number}
                                  description={
                                    conRight.description &&
                                    conRight.description.description ? (
                                      <span>
                                        {conRight.description.description}
                                      </span>
                                    ) : null
                                  }
                                  {...(conRight.url
                                    ? { url: conRight.url }
                                    : {})}
                                  titleClass={conRight.cardTitle
                                    .toLowerCase()
                                    .replace(/[\s:,]+/g, "-")}
                                />
                              ) : null}
                              {conRight.questionsAndAnswers ? (
                                <FAQ
                                  content={conRight.questionsAndAnswers}
                                  data={data}
                                />
                              ) : null}
                              {conRight.videoName ? (
                                <ModalVideo
                                  key={conRight.id}
                                  title={conRight.videoName}
                                  youtubeID={conRight.videoId}
                                  caption={conRight.videoCaption}
                                  videoImageThumbnail={
                                    conRight.videoImageThumbnail
                                  }
                                />
                              ) : null}
                            </div>
                          ))}
                      </Col>
                    </Row>
                  </Container>
                </Container>
              </>
            );
          } else if (gridItem.layout === "2 Column - 50/50") {
            return (
              <>
                <Container
                  fluid
                  className={gridItem.name
                    .toLowerCase()
                    .replace(/[\s:\/,&+]+/g, "-")}
                >
                  <Container>
                    {gridItem.showTitle ? <h2>{gridItem.name}</h2> : null}
                    <Row
                      className={gridItem.name
                        .toLowerCase()
                        .replace(/[\s:\/,&+]+/g, "-")}
                    >
                      <Col xs={12} md={6} key={gridItem.id}>
                        {/* ************ 2 COLUMN - 50/50 - LEFT COLUMN *********** */}
                        {Array.isArray(gridItem.leftColumn) &&
                          gridItem.leftColumn.map((conLeft) => (
                            <div
                              className={
                                conLeft.name
                                  ? conLeft.name
                                      .toLowerCase()
                                      .replace(/[\s:,]+/g, "-")
                                  : null
                              }
                              id={conLeft.styleId ? conLeft.styleId : null}
                            >
                              {conLeft.showName ? (
                                <h2>{conLeft.name}</h2>
                              ) : null}
                              {conLeft.body ? (
                                <p>{renderRichText(conLeft.body, options)}</p>
                              ) : null}
                              {conLeft.image ? (
                                <img
                                  loading="lazy"
                                  alt={conLeft.name}
                                  src={conLeft.image.url}
                                />
                              ) : null}
                              {conLeft.caption ? (
                                <caption>{conLeft.caption}</caption>
                              ) : conLeft.captionRichText ? (
                                <caption>
                                  {renderRichText(
                                    conLeft.captionRichText,
                                    options
                                  )}
                                </caption>
                              ) : null}
                              {/* ********************************************** STATCARD ********************************************** */}
                              {conLeft.cardTitle ? (
                                <StatCard
                                  key={conLeft.id}
                                  icon={
                                    conLeft.icon ? (
                                      <img
                                        loading="lazy"
                                        alt={conLeft.cardTitle}
                                        src={conLeft.icon.url}
                                        className="card-icons"
                                      />
                                    ) : null
                                  }
                                  url={conLeft.url}
                                  title={conLeft.cardTitle}
                                  subtitle={conLeft.number}
                                  description={
                                    conLeft.description &&
                                    conLeft.description.description ? (
                                      <span>
                                        {conLeft.description.description}
                                      </span>
                                    ) : null
                                  }
                                  {...(conLeft.url ? { url: conLeft.url } : {})}
                                  titleClass={conLeft.cardTitle
                                    .toLowerCase()
                                    .replace(/[\s:,]+/g, "-")}
                                />
                              ) : null}
                              {conLeft.questionsAndAnswers ? (
                                <FAQ
                                  content={conLeft.questionsAndAnswers}
                                  data={data}
                                />
                              ) : null}
                              {/* ********************************************** VIDEO ********************************************** */}
                              {conLeft.videoName ? (
                                <ModalVideo
                                  key={conLeft.id}
                                  title={conLeft.videoName}
                                  youtubeID={conLeft.videoId}
                                  caption={conLeft.videoCaption}
                                  videoImageThumbnail={
                                    conLeft.videoImageThumbnail
                                  }
                                />
                              ) : null}
                            </div>
                          ))}
                      </Col>
                      <Col xs={12} md={6} key={gridItem.id}>
                        {/* ***************************** 2 COLUMN - 50/50 - RIGHT COLUMN ***************************** */}
                        {Array.isArray(gridItem.rightColumn) &&
                          gridItem.rightColumn.map((conRight) => (
                            <div
                              className={
                                conRight.name
                                  ? conRight.name
                                      .toLowerCase()
                                      .replace(/[\s:,]+/g, "-")
                                  : null
                              }
                              id={conRight.styleId ? conRight.styleId : null}
                            >
                              {conRight.showName ? (
                                <h2>{conRight.name}</h2>
                              ) : null}
                              {conRight.body ? (
                                <p>{renderRichText(conRight.body, options)}</p>
                              ) : null}
                              {conRight.image ? (
                                <img
                                  loading="lazy"
                                  alt={conRight.name}
                                  src={conRight.image.url}
                                />
                              ) : null}
                              {conRight.caption ? (
                                <caption>{conRight.caption}</caption>
                              ) : conRight.captionRichText ? (
                                <caption>
                                  {renderRichText(
                                    conRight.captionRichText,
                                    options
                                  )}
                                </caption>
                              ) : null}
                              {/* ********************************************** STATCARD ********************************************** */}
                              {conRight.cardTitle ? (
                                <StatCard
                                  key={conRight.id}
                                  icon={
                                    conRight.icon ? (
                                      <img
                                        loading="lazy"
                                        alt={conRight.cardTitle}
                                        src={conRight.icon.url}
                                        className="card-icons"
                                      />
                                    ) : null
                                  }
                                  url={conRight.url}
                                  title={conRight.cardTitle}
                                  subtitle={conRight.number}
                                  description={
                                    conRight.description &&
                                    conRight.description.description ? (
                                      <span>
                                        {conRight.description.description}
                                      </span>
                                    ) : null
                                  }
                                  {...(conRight.url
                                    ? { url: conRight.url }
                                    : {})}
                                  titleClass={conRight.cardTitle
                                    .toLowerCase()
                                    .replace(/[\s:,]+/g, "-")}
                                />
                              ) : null}

                              {conRight.carouselSlideContainer ? (
                                <Slideshow
                                  content={conRight.carouselSlideContainer}
                                />
                              ) : null}

                              {conRight.questionsAndAnswers ? (
                                <FAQ
                                  content={conRight.questionsAndAnswers}
                                  data={data}
                                />
                              ) : null}

                              {/* ********************************************** VIDEO ********************************************** */}
                              {conRight.videoName ? (
                                <ModalVideo
                                  key={conRight.id}
                                  title={conRight.videoName}
                                  youtubeID={conRight.videoId}
                                  caption={conRight.videoCaption}
                                  videoImageThumbnail={
                                    conRight.videoImageThumbnail
                                  }
                                />
                              ) : null}
                            </div>
                          ))}
                      </Col>
                    </Row>
                  </Container>
                </Container>
              </>
            );
          } else if (gridItem.layout === "3 Column - 33/33/33") {
            return (
              <>
                <Container
                  fluid
                  className={gridItem.name
                    .toLowerCase()
                    .replace(/[\s:\/,&+]+/g, "-")}
                >
                  <Container>
                    {gridItem.showTitle ? <h2>{gridItem.name}</h2> : null}
                    <Row>
                      <Col xs={12} md={4} key={gridItem.id}>
                        {/* ***************************** 3 COLUMN - 33/33/33 - LEFT COLUMN ***************************** */}
                        {Array.isArray(gridItem.leftColumn) &&
                          gridItem.leftColumn.map((conLeft) => (
                            <div id={conLeft.styleId ? conLeft.styleId : null}>
                              {conLeft.showName ? (
                                <h2>{conLeft.name}</h2>
                              ) : null}
                              {conLeft.body ? (
                                <p>{renderRichText(conLeft.body, options)}</p>
                              ) : null}
                              {conLeft.image ? (
                                <img
                                  loading="lazy"
                                  alt={conLeft.name}
                                  src={conLeft.image.url}
                                />
                              ) : null}
                              {conLeft.caption ? (
                                <caption>{conLeft.caption}</caption>
                              ) : conLeft.captionRichText ? (
                                <caption>
                                  {renderRichText(
                                    conLeft.captionRichText,
                                    options
                                  )}
                                </caption>
                              ) : null}

                              {/* ********************************************** VIDEO ********************************************** */}
                              {conLeft.videoName ? (
                                <ModalVideo
                                  key={conLeft.id}
                                  title={conLeft.videoName}
                                  youtubeID={conLeft.videoId}
                                  caption={conLeft.videoCaption}
                                  videoImageThumbnail={
                                    conLeft.videoImageThumbnail
                                  }
                                />
                              ) : null}

                              {/* ********************************************** STATCARD ********************************************** */}
                              {conLeft.cardTitle ? (
                                <StatCard
                                  key={conLeft.id}
                                  title={conLeft.cardTitle}
                                  subtitle={conLeft.number}
                                  url={conLeft.url}
                                  description={
                                    conLeft.description &&
                                    conLeft.description.description ? (
                                      <span>
                                        {conLeft.description.description}
                                      </span>
                                    ) : null
                                  }
                                  {...(conLeft.url ? { url: conLeft.url } : {})}
                                  titleClass={conLeft.cardTitle
                                    .toLowerCase()
                                    .replace(/[\s:,]+/g, "-")}
                                />
                              ) : null}
                              {conLeft.questionsAndAnswers ? (
                                <FAQ
                                  content={conLeft.questionsAndAnswers}
                                  data={data}
                                />
                              ) : null}
                            </div>
                          ))}
                      </Col>
                      <Col xs={12} md={4} key={gridItem.id}>
                        {/* ***************************** 3 COLUMN - 33/33/33 - MIDDLE COLUMN ***************************** */}
                        {Array.isArray(gridItem.middleColumn) &&
                          gridItem.middleColumn.map((conMid) => (
                            <div id={conMid.styleId ? conMid.styleId : null}>
                              {conMid.showName ? <h2>{conMid.name}</h2> : null}
                              {conMid.body ? (
                                <p>{renderRichText(conMid.body, options)}</p>
                              ) : null}
                              {conMid.image ? (
                                <img
                                  loading="lazy"
                                  alt={conMid.name}
                                  src={conMid.image.url}
                                />
                              ) : null}
                              {conMid.caption ? (
                                <caption>{conMid.caption}</caption>
                              ) : conMid.captionRichText ? (
                                <caption>
                                  {renderRichText(
                                    conMid.captionRichText,
                                    options
                                  )}
                                </caption>
                              ) : null}

                              {/* ********************************************** VIDEO ********************************************** */}
                              {conMid.videoName ? (
                                <ModalVideo
                                  key={conMid.id}
                                  title={conMid.videoName}
                                  youtubeID={conMid.videoId}
                                  caption={conMid.videoCaption}
                                  videoImageThumbnail={
                                    conMid.videoImageThumbnail
                                  }
                                />
                              ) : null}

                              {/* ********************************************** STATCARD ********************************************** */}
                              {conMid.cardTitle ? (
                                <StatCard
                                  key={conMid.id}
                                  title={conMid.cardTitle}
                                  subtitle={conMid.number}
                                  url={conMid.url}
                                  description={
                                    conMid.description &&
                                    conMid.description.description ? (
                                      <span>
                                        {conMid.description.description}
                                      </span>
                                    ) : null
                                  }
                                  {...(conMid.url ? { url: conMid.url } : {})}
                                  titleClass={conMid.cardTitle
                                    .toLowerCase()
                                    .replace(/[\s:,]+/g, "-")}
                                />
                              ) : null}
                              {conMid.questionsAndAnswers ? (
                                <FAQ
                                  content={conMid.questionsAndAnswers}
                                  data={data}
                                />
                              ) : null}
                            </div>
                          ))}
                      </Col>
                      <Col xs={12} md={4} key={gridItem.id}>
                        {/* ***************************** 3 COLUMN - 33/33/33 - RIGHT COLUMN ***************************** */}
                        {Array.isArray(gridItem.rightColumn) &&
                          gridItem.rightColumn.map((conRight) => (
                            <div
                              id={conRight.styleId ? conRight.styleId : null}
                            >
                              {conRight.showName ? (
                                <h2>{conRight.name}</h2>
                              ) : null}
                              {conRight.body ? (
                                <p>{renderRichText(conRight.body, options)}</p>
                              ) : null}
                              {conRight.image ? (
                                <img
                                  loading="lazy"
                                  alt={conRight.name}
                                  src={conRight.image.url}
                                />
                              ) : null}
                              {conRight.caption ? (
                                <caption>{conRight.caption}</caption>
                              ) : conRight.captionRichText ? (
                                <caption>
                                  {renderRichText(
                                    conRight.captionRichText,
                                    options
                                  )}
                                </caption>
                              ) : null}

                              {/* ********************************************** VIDEO ********************************************** */}
                              {conRight.videoName ? (
                                <ModalVideo
                                  key={conRight.id}
                                  title={conRight.videoName}
                                  youtubeID={conRight.videoId}
                                  caption={conRight.videoCaption}
                                  videoImageThumbnail={
                                    conRight.videoImageThumbnail
                                  }
                                />
                              ) : null}

                              {/* ********************************************** STATCARD ********************************************** */}
                              {conRight.cardTitle ? (
                                <StatCard
                                  key={conRight.id}
                                  title={conRight.cardTitle}
                                  subtitle={conRight.number}
                                  url={conRight.url}
                                  description={
                                    conRight.description &&
                                    conRight.description.description ? (
                                      <span>
                                        {conRight.description.description}
                                      </span>
                                    ) : null
                                  }
                                  {...(conRight.url
                                    ? { url: conRight.url }
                                    : {})}
                                  titleClass={conRight.cardTitle
                                    .toLowerCase()
                                    .replace(/[\s:,]+/g, "-")}
                                />
                              ) : null}
                              {conRight.questionsAndAnswers ? (
                                <FAQ
                                  content={conRight.questionsAndAnswers}
                                  data={data}
                                />
                              ) : null}
                            </div>
                          ))}
                      </Col>
                    </Row>
                  </Container>
                </Container>
              </>
            );
          } else if (gridItem.layout === "3 Column - 25/25/50") {
            return (
              <>
                <Container
                  fluid
                  className={gridItem.name
                    .toLowerCase()
                    .replace(/[\s:\/,&+]+/g, "-")}
                >
                  <Container>
                    {gridItem.showTitle ? <h2>{gridItem.name}</h2> : null}
                    <Row>
                      <Col xs={12} md={3} key={gridItem.id}>
                        {/* ***************************** 3 COLUMN - 25/25/50 - LEFT COLUMN ***************************** */}
                        {Array.isArray(gridItem.leftColumn) &&
                          gridItem.leftColumn.map((conLeft) => (
                            <div id={conLeft.styleId ? conLeft.styleId : null}>
                              {conLeft.showName ? (
                                <h2>{conLeft.name}</h2>
                              ) : null}
                              {conLeft.body ? (
                                <p>{renderRichText(conLeft.body, options)}</p>
                              ) : null}
                              {conLeft.image ? (
                                <img
                                  loading="lazy"
                                  alt={conLeft.name}
                                  src={conLeft.image.url}
                                />
                              ) : null}
                              {conLeft.caption ? (
                                <caption>{conLeft.caption}</caption>
                              ) : conLeft.captionRichText ? (
                                <caption>
                                  {renderRichText(
                                    conLeft.captionRichText,
                                    options
                                  )}
                                </caption>
                              ) : null}
                              {/* ********************************************** STATCARD ********************************************** */}
                              {conLeft.cardTitle ? (
                                <StatCard
                                  key={conLeft.id}
                                  icon={
                                    conLeft.icon ? (
                                      <img
                                        loading="lazy"
                                        alt={conLeft.cardTitle}
                                        src={conLeft.icon.url}
                                        className="card-icons"
                                      />
                                    ) : null
                                  }
                                  url={conLeft.url}
                                  title={conLeft.cardTitle}
                                  subtitle={conLeft.number}
                                  // description={conLeft.description.description}

                                  description={
                                    conLeft.description &&
                                    conLeft.description.description ? (
                                      <span>
                                        {conLeft.description.description}
                                      </span>
                                    ) : null
                                  }
                                  {...(conLeft.url ? { url: conLeft.url } : {})}
                                  titleClass={conLeft.cardTitle
                                    .toLowerCase()
                                    .replace(/[\s:,]+/g, "-")}
                                />
                              ) : null}
                              {conLeft.questionsAndAnswers ? (
                                <FAQ
                                  content={conLeft.questionsAndAnswers}
                                  data={data}
                                />
                              ) : null}
                            </div>
                          ))}
                      </Col>
                      <Col xs={12} md={3} key={gridItem.id}>
                        {/* ***************************** 3 COLUMN - 25/25/50 - RIGHT COLUMN ***************************** */}
                        {Array.isArray(gridItem.middleColumn) &&
                          gridItem.middleColumn.map((conMid) => (
                            <div id={conMid.styleId ? conMid.styleId : null}>
                              {conMid.showName ? <h2>{conMid.name}</h2> : null}
                              {conMid.body ? (
                                <p>{renderRichText(conMid.body, options)}</p>
                              ) : null}
                              {conMid.image ? (
                                <img
                                  loading="lazy"
                                  alt={conMid.name}
                                  src={conMid.image.url}
                                />
                              ) : null}
                              {conMid.caption ? (
                                <caption>{conMid.caption}</caption>
                              ) : conMid.captionRichText ? (
                                <caption>
                                  {renderRichText(
                                    conMid.captionRichText,
                                    options
                                  )}
                                </caption>
                              ) : null}
                              {/* ********************************************** STATCARD ********************************************** */}
                              {conMid.cardTitle ? (
                                <StatCard
                                  key={conMid.id}
                                  icon={
                                    conMid.icon ? (
                                      <img
                                        loading="lazy"
                                        alt={conMid.cardTitle}
                                        src={conMid.icon.url}
                                        className="card-icons"
                                      />
                                    ) : null
                                  }
                                  url={conMid.url}
                                  title={conMid.cardTitle}
                                  subtitle={conMid.number}
                                  description={conMid.description.description}
                                  {...(conMid.url ? { url: conMid.url } : {})}
                                  titleClass={conMid.cardTitle
                                    .toLowerCase()
                                    .replace(/[\s:,]+/g, "-")}
                                />
                              ) : null}
                              {conMid.questionsAndAnswers ? (
                                <FAQ
                                  content={conMid.questionsAndAnswers}
                                  data={data}
                                />
                              ) : null}
                            </div>
                          ))}
                      </Col>
                      <Col xs={12} md={6} key={gridItem.id}>
                        {/* ***************************** 3 COLUMN - 25/25/50 - RIGHT COLUMN ***************************** */}
                        {Array.isArray(gridItem.rightColumn) &&
                          gridItem.rightColumn.map((conRight) => (
                            <div
                              id={conRight.styleId ? conRight.styleId : null}
                            >
                              {conRight.showName ? (
                                <h2>{conRight.name}</h2>
                              ) : null}
                              {conRight.body ? (
                                <p>{renderRichText(conRight.body, options)}</p>
                              ) : null}
                              {conRight.image ? (
                                <img
                                  loading="lazy"
                                  alt={conRight.name}
                                  src={conRight.image.url}
                                />
                              ) : null}
                              {conRight.caption ? (
                                <caption>{conRight.caption}</caption>
                              ) : conRight.captionRichText ? (
                                <caption>
                                  {renderRichText(
                                    conRight.captionRichText,
                                    options
                                  )}
                                </caption>
                              ) : null}
                              {/* ********************************************** STATCARD ********************************************** */}
                              {conRight.cardTitle ? (
                                <StatCard
                                  key={conRight.id}
                                  icon={
                                    conRight.icon ? (
                                      <img
                                        loading="lazy"
                                        alt={conRight.cardTitle}
                                        src={conRight.icon.url}
                                        className="card-icons"
                                      />
                                    ) : null
                                  }
                                  url={conRight.url}
                                  title={conRight.cardTitle}
                                  subtitle={conRight.number}
                                  description={
                                    conRight.description &&
                                    conRight.description.description ? (
                                      <span>
                                        {conRight.description.description}
                                      </span>
                                    ) : null
                                  }
                                  {...(conRight.url
                                    ? { url: conRight.url }
                                    : {})}
                                  titleClass={conRight.cardTitle
                                    .toLowerCase()
                                    .replace(/[\s:,]+/g, "-")}
                                />
                              ) : null}
                              {conRight.questionsAndAnswers ? (
                                <FAQ
                                  content={conRight.questionsAndAnswers}
                                  data={data}
                                />
                              ) : null}
                            </div>
                          ))}
                      </Col>
                    </Row>
                  </Container>
                </Container>
              </>
            );
          } else if (gridItem.layout === "3 Column - 25/50/25") {
            return (
              <>
                <Container
                  fluid
                  className={gridItem.name
                    .toLowerCase()
                    .replace(/[\s:\/,&+]+/g, "-")}
                >
                  <Container>
                    {gridItem.showTitle ? <h2>{gridItem.name}</h2> : null}
                    <Row>
                      <Col xs={12} md={3} key={gridItem.id}>
                        {/* ***************************** 3 COLUMN - 25/50/25 - LEFT COLUMN ***************************** */}
                        {Array.isArray(gridItem.leftColumn) &&
                          gridItem.leftColumn.map((conLeft) => (
                            <div id={conLeft.styleId ? conLeft.styleId : null}>
                              {conLeft.showName ? (
                                <h2>{conLeft.name}</h2>
                              ) : null}
                              {conLeft.body ? (
                                <p>{renderRichText(conLeft.body, options)}</p>
                              ) : null}
                              {conLeft.image ? (
                                <img
                                  loading="lazy"
                                  alt={conLeft.name}
                                  src={conLeft.image.url}
                                />
                              ) : null}
                              {conLeft.caption ? (
                                <caption>{conLeft.caption}</caption>
                              ) : conLeft.captionRichText ? (
                                <caption>
                                  {renderRichText(
                                    conLeft.captionRichText,
                                    options
                                  )}
                                </caption>
                              ) : null}
                              {/* ********************************************** STATCARD ********************************************** */}
                              {conLeft.cardTitle ? (
                                <StatCard
                                  key={conLeft.id}
                                  icon={
                                    conLeft.icon ? (
                                      <img
                                        loading="lazy"
                                        alt={conLeft.cardTitle}
                                        src={conLeft.icon.url}
                                        className="card-icons"
                                      />
                                    ) : null
                                  }
                                  url={conLeft.url}
                                  title={conLeft.cardTitle}
                                  subtitle={conLeft.number}
                                  description={
                                    conLeft.description &&
                                    conLeft.description.description ? (
                                      <span>
                                        {conLeft.description.description}
                                      </span>
                                    ) : null
                                  }
                                  {...(conLeft.url ? { url: conLeft.url } : {})}
                                  titleClass={conLeft.cardTitle
                                    .toLowerCase()
                                    .replace(/[\s:,]+/g, "-")}
                                />
                              ) : null}
                              {conLeft.questionsAndAnswers ? (
                                <FAQ
                                  content={conLeft.questionsAndAnswers}
                                  data={data}
                                />
                              ) : null}
                            </div>
                          ))}
                      </Col>
                      <Col xs={12} md={6} key={gridItem.id}>
                        {/* ***************************** 3 COLUMN - 25/50/25 - Mid COLUMN ***************************** */}
                        {Array.isArray(gridItem.middleColumn) &&
                          gridItem.middleColumn.map((conMid) => (
                            <div id={conMid.styleId ? conMid.styleId : null}>
                              {conMid.showName ? <h2>{conMid.name}</h2> : null}
                              {conMid.body ? (
                                <p>{renderRichText(conMid.body, options)}</p>
                              ) : null}
                              {conMid.image ? (
                                <img
                                  loading="lazy"
                                  alt={conMid.name}
                                  src={conMid.image.url}
                                />
                              ) : null}
                              {conMid.caption ? (
                                <caption>{conMid.caption}</caption>
                              ) : conMid.captionRichText ? (
                                <caption>
                                  {renderRichText(
                                    conMid.captionRichText,
                                    options
                                  )}
                                </caption>
                              ) : null}
                              {/* ********************************************** STATCARD ********************************************** */}
                              {conMid.cardTitle ? (
                                <StatCard
                                  key={conMid.id}
                                  icon={
                                    conMid.icon ? (
                                      <img
                                        loading="lazy"
                                        alt={conMid.cardTitle}
                                        src={conMid.icon.url}
                                        className="card-icons"
                                      />
                                    ) : null
                                  }
                                  url={conMid.url}
                                  title={conMid.cardTitle}
                                  subtitle={conMid.number}
                                  description={
                                    conMid.description &&
                                    conMid.description.description ? (
                                      <span>
                                        {conMid.description.description}
                                      </span>
                                    ) : null
                                  }
                                  {...(conMid.url ? { url: conMid.url } : {})}
                                  titleClass={conMid.cardTitle
                                    .toLowerCase()
                                    .replace(/[\s:,]+/g, "-")}
                                />
                              ) : null}
                              {conMid.questionsAndAnswers ? (
                                <FAQ
                                  content={conMid.questionsAndAnswers}
                                  data={data}
                                />
                              ) : null}
                            </div>
                          ))}
                      </Col>
                      <Col xs={12} md={3} key={gridItem.id}>
                        {/* ***************************** 3 COLUMN - 25/50/25 - RIGHT COLUMN ***************************** */}
                        {Array.isArray(gridItem.rightColumn) &&
                          gridItem.rightColumn.map((conRight) => (
                            <div
                              id={conRight.styleId ? conRight.styleId : null}
                            >
                              {conRight.showName ? (
                                <h2>{conRight.name}</h2>
                              ) : null}
                              {conRight.body ? (
                                <p>{renderRichText(conRight.body, options)}</p>
                              ) : null}
                              {conRight.image ? (
                                <img
                                  loading="lazy"
                                  alt={conRight.name}
                                  src={conRight.image.url}
                                />
                              ) : null}
                              {conRight.caption ? (
                                <caption>{conRight.caption}</caption>
                              ) : conRight.captionRichText ? (
                                <caption>
                                  {renderRichText(
                                    conRight.captionRichText,
                                    options
                                  )}
                                </caption>
                              ) : null}
                              {/* ********************************************** STATCARD ********************************************** */}
                              {conRight.cardTitle ? (
                                <StatCard
                                  key={conRight.id}
                                  icon={
                                    conRight.icon ? (
                                      <img
                                        loading="lazy"
                                        alt={conRight.cardTitle}
                                        src={conRight.icon.url}
                                        className="card-icons"
                                      />
                                    ) : null
                                  }
                                  title={conRight.cardTitle}
                                  subtitle={conRight.number}
                                  url={conRight.url}
                                  description={
                                    conRight.description &&
                                    conRight.description.description ? (
                                      <span>
                                        {conRight.description.description}
                                      </span>
                                    ) : null
                                  }
                                  {...(conRight.url
                                    ? { url: conRight.url }
                                    : {})}
                                  titleClass={conRight.cardTitle
                                    .toLowerCase()
                                    .replace(/[\s:,]+/g, "-")}
                                />
                              ) : null}
                              {conRight.questionsAndAnswers ? (
                                <FAQ
                                  content={conRight.questionsAndAnswers}
                                  data={data}
                                />
                              ) : null}
                            </div>
                          ))}
                      </Col>
                    </Row>
                  </Container>
                </Container>
              </>
            );
          } else if (gridItem.layout === "3 Column - 50/25/25") {
            return (
              <>
                <Container
                  fluid
                  className={gridItem.name
                    .toLowerCase()
                    .replace(/[\s:\/,&+]+/g, "-")}
                >
                  <Container>
                    {gridItem.showTitle ? <h2>{gridItem.name}</h2> : null}
                    <Row>
                      <Col xs={12} md={6} key={gridItem.id}>
                        {/* ***************************** 3 COLUMN - 50/25/25 - LEFT COLUMN ***************************** */}
                        {Array.isArray(gridItem.leftColumn) &&
                          gridItem.leftColumn.map((conLeft) => (
                            <div id={conLeft.styleId ? conLeft.styleId : null}>
                              {conLeft.showName ? (
                                <h2>{conLeft.name}</h2>
                              ) : null}
                              {conLeft.body ? (
                                <p>{renderRichText(conLeft.body, options)}</p>
                              ) : null}
                              {conLeft.image ? (
                                <img
                                  loading="lazy"
                                  alt={conLeft.name}
                                  src={conLeft.image.url}
                                />
                              ) : null}
                              {conLeft.caption ? (
                                <caption>{conLeft.caption}</caption>
                              ) : conLeft.captionRichText ? (
                                <caption>
                                  {renderRichText(
                                    conLeft.captionRichText,
                                    options
                                  )}
                                </caption>
                              ) : null}
                              {/* ********************************************** STATCARD ********************************************** */}
                              {conLeft.cardTitle ? (
                                <StatCard
                                  key={conLeft.id}
                                  icon={
                                    conLeft.icon ? (
                                      <img
                                        loading="lazy"
                                        alt={conLeft.cardTitle}
                                        src={conLeft.icon.url}
                                        className="card-icons"
                                      />
                                    ) : null
                                  }
                                  title={conLeft.cardTitle}
                                  subtitle={conLeft.number}
                                  url={conLeft.url}
                                  description={
                                    conLeft.description &&
                                    conLeft.description.description ? (
                                      <span>
                                        {conLeft.description.description}
                                      </span>
                                    ) : null
                                  }
                                  {...(conLeft.url ? { url: conLeft.url } : {})}
                                  titleClass={conLeft.cardTitle
                                    .toLowerCase()
                                    .replace(/[\s:,]+/g, "-")}
                                />
                              ) : null}
                              {conLeft.questionsAndAnswers ? (
                                <FAQ
                                  content={conLeft.questionsAndAnswers}
                                  data={data}
                                />
                              ) : null}
                            </div>
                          ))}
                      </Col>
                      <Col xs={12} md={3} key={gridItem.id}>
                        {/* ***************************** 3 COLUMN - 50/25/25 - RIGHT COLUMN ***************************** */}
                        {Array.isArray(gridItem.middleColumn) &&
                          gridItem.middleColumn.map((conMid) => (
                            <div id={conMid.styleId ? conMid.styleId : null}>
                              {conMid.showName ? <h2>{conMid.name}</h2> : null}
                              {conMid.body ? (
                                <p>{renderRichText(conMid.body, options)}</p>
                              ) : null}
                              {conMid.image ? (
                                <img
                                  loading="lazy"
                                  alt={conMid.name}
                                  src={conMid.image.url}
                                />
                              ) : null}
                              {conMid.caption ? (
                                <caption>{conMid.caption}</caption>
                              ) : conMid.captionRichText ? (
                                <caption>
                                  {renderRichText(
                                    conMid.captionRichText,
                                    options
                                  )}
                                </caption>
                              ) : null}
                              {/* ********************************************** STATCARD ********************************************** */}
                              {conMid.cardTitle ? (
                                <StatCard
                                  key={conMid.id}
                                  icon={
                                    conMid.icon ? (
                                      <img
                                        loading="lazy"
                                        alt={conMid.cardTitle}
                                        src={conMid.icon.url}
                                        className="card-icons"
                                      />
                                    ) : null
                                  }
                                  title={conMid.cardTitle}
                                  subtitle={conMid.number}
                                  url={conMid.url}
                                  description={
                                    conMid.description &&
                                    conMid.description.description ? (
                                      <span>
                                        {conMid.description.description}
                                      </span>
                                    ) : null
                                  }
                                  {...(conMid.url ? { url: conMid.url } : {})}
                                  titleClass={conMid.cardTitle
                                    .toLowerCase()
                                    .replace(/[\s:,]+/g, "-")}
                                />
                              ) : null}
                              {conMid.questionsAndAnswers ? (
                                <FAQ
                                  content={conMid.questionsAndAnswers}
                                  data={data}
                                />
                              ) : null}
                            </div>
                          ))}
                      </Col>
                      <Col xs={12} md={3} key={gridItem.id}>
                        {/* ***************************** 3 COLUMN - 50/25/25 - RIGHT COLUMN ***************************** */}
                        {Array.isArray(gridItem.rightColumn) &&
                          gridItem.rightColumn.map((conRight) => (
                            <div
                              id={conRight.styleId ? conRight.styleId : null}
                            >
                              {conRight.showName ? (
                                <h2>{conRight.name}</h2>
                              ) : null}
                              {conRight.body ? (
                                <p>{renderRichText(conRight.body, options)}</p>
                              ) : null}
                              {conRight.image ? (
                                <img
                                  loading="lazy"
                                  alt={conRight.name}
                                  src={conRight.image.url}
                                />
                              ) : null}
                              {conRight.caption ? (
                                <caption>{conRight.caption}</caption>
                              ) : conRight.captionRichText ? (
                                <caption>
                                  {renderRichText(
                                    conRight.captionRichText,
                                    options
                                  )}
                                </caption>
                              ) : null}
                              {/* ********************************************** VIDEO ********************************************** */}
                              {conRight.videoName ? (
                                <ModalVideo
                                  key={conRight.id}
                                  title={conRight.videoName}
                                  youtubeID={conRight.videoId}
                                  caption={conRight.videoCaption}
                                  videoImageThumbnail={
                                    conRight.videoImageThumbnail
                                  }
                                />
                              ) : null}
                              {/* ********************************************** STATCARD ********************************************** */}
                              {conRight.cardTitle ? (
                                <StatCard
                                  key={conRight.id}
                                  icon={
                                    conRight.icon ? (
                                      <img
                                        loading="lazy"
                                        alt={conRight.cardTitle}
                                        src={conRight.icon.url}
                                        className="card-icons"
                                      />
                                    ) : null
                                  }
                                  title={conRight.cardTitle}
                                  subtitle={conRight.number}
                                  url={conRight.url}
                                  description={
                                    conRight.description &&
                                    conRight.description.description ? (
                                      <span>
                                        {conRight.description.description}
                                      </span>
                                    ) : null
                                  }
                                  {...(conRight.url
                                    ? { url: conRight.url }
                                    : {})}
                                  titleClass={conRight.cardTitle
                                    .toLowerCase()
                                    .replace(/[\s:,]+/g, "-")}
                                />
                              ) : null}
                              {conRight.questionsAndAnswers ? (
                                <FAQ
                                  content={conRight.questionsAndAnswers}
                                  data={data}
                                />
                              ) : null}
                            </div>
                          ))}
                      </Col>
                    </Row>
                  </Container>
                </Container>
              </>
            );
          } else if (gridItem.layout === "4 Column - 25/25/25/25") {
            return (
              <>
                <Container
                  fluid
                  className={gridItem.name
                    .toLowerCase()
                    .replace(/[\s:\/,&+]+/g, "-")}
                >
                  <Container>
                    {gridItem.showTitle ? <h2>{gridItem.name}</h2> : null}
                    <Row>
                      <Col xs={12} md={3} key={gridItem.id}>
                        {/* ***************************** 4 Column - 25/25/25/25 - LEFT COLUMN ***************************** */}
                        {Array.isArray(gridItem.leftColumn) &&
                          gridItem.leftColumn.map((conLeft) => (
                            <div id={conLeft.styleId ? conLeft.styleId : null}>
                              {conLeft.showName ? (
                                <h2>{conLeft.name}</h2>
                              ) : null}
                              {conLeft.body ? (
                                <p>{renderRichText(conLeft.body, options)}</p>
                              ) : null}

                              {conLeft.image ? (
                                <img
                                  loading="lazy"
                                  alt={conLeft.name}
                                  src={conLeft.image.url}
                                />
                              ) : null}
                              {conLeft.caption ? (
                                <caption>{conLeft.caption}</caption>
                              ) : conLeft.captionRichText ? (
                                <caption>
                                  {renderRichText(
                                    conLeft.captionRichText,
                                    options
                                  )}
                                </caption>
                              ) : null}

                              {/* ********************************************** VIDEO ********************************************** */}
                              {conLeft.videoName ? (
                                <ModalVideo
                                  key={conLeft.id}
                                  title={conLeft.videoName}
                                  youtubeID={conLeft.videoId}
                                  caption={conLeft.videoCaption}
                                  videoImageThumbnail={
                                    conLeft.videoImageThumbnail
                                  }
                                />
                              ) : null}

                              {/* ********************************************** STATCARD ********************************************** */}
                              {conLeft.cardTitle ? (
                                <StatCard
                                  key={conLeft.id}
                                  icon={
                                    conLeft.icon ? (
                                      <img
                                        loading="lazy"
                                        alt={conLeft.cardTitle}
                                        src={conLeft.icon.url}
                                        className="card-icons"
                                      />
                                    ) : null
                                  }
                                  title={conLeft.cardTitle}
                                  subtitle={conLeft.number}
                                  url={conLeft.url}
                                  description={
                                    conLeft.description &&
                                    conLeft.description.description ? (
                                      <span>
                                        {conLeft.description.description}
                                      </span>
                                    ) : null
                                  }
                                  {...(conLeft.url ? { url: conLeft.url } : {})}
                                  titleClass={conLeft.cardTitle
                                    .toLowerCase()
                                    .replace(/[\s:,]+/g, "-")}
                                />
                              ) : null}
                              {conLeft.questionsAndAnswers ? (
                                <FAQ
                                  content={conLeft.questionsAndAnswers}
                                  data={data}
                                />
                              ) : null}
                            </div>
                          ))}
                      </Col>
                      <Col xs={12} md={3} key={gridItem.id}>
                        {/* ***************************** 4 Column - 25/25/25/25 - RIGHT COLUMN ***************************** */}
                        {Array.isArray(gridItem.middleLeftColumn) &&
                          gridItem.middleLeftColumn.map((conMidLeft) => (
                            <div
                              id={
                                conMidLeft.styleId ? conMidLeft.styleId : null
                              }
                            >
                              {conMidLeft.showName ? (
                                <h2>{conMidLeft.name}</h2>
                              ) : null}
                              {conMidLeft.body ? (
                                <p>
                                  {renderRichText(conMidLeft.body, options)}
                                </p>
                              ) : null}

                              {conMidLeft.image ? (
                                <img
                                  loading="lazy"
                                  alt={conMidLeft.name}
                                  src={conMidLeft.image.url}
                                />
                              ) : null}
                              {conMidLeft.caption ? (
                                <caption>{conMidLeft.caption}</caption>
                              ) : conMidLeft.captionRichText ? (
                                <caption>
                                  {renderRichText(
                                    conMidLeft.captionRichText,
                                    options
                                  )}
                                </caption>
                              ) : null}

                              {/* ********************************************** VIDEO ********************************************** */}
                              {conMidLeft.videoName ? (
                                <ModalVideo
                                  key={conMidLeft.id}
                                  title={conMidLeft.videoName}
                                  youtubeID={conMidLeft.videoId}
                                  caption={conMidLeft.videoCaption}
                                  videoImageThumbnail={
                                    conMidLeft.videoImageThumbnail
                                  }
                                />
                              ) : null}
                              {/* ********************************************** STATCARD ********************************************** */}
                              {conMidLeft.cardTitle ? (
                                <StatCard
                                  key={conMidLeft.id}
                                  icon={
                                    conMidLeft.icon ? (
                                      <img
                                        loading="lazy"
                                        alt={conMidLeft.cardTitle}
                                        src={conMidLeft.icon.url}
                                        className="card-icons"
                                      />
                                    ) : null
                                  }
                                  title={conMidLeft.cardTitle}
                                  subtitle={conMidLeft.number}
                                  url={conMidLeft.url}
                                  description={
                                    conMidLeft.description &&
                                    conMidLeft.description.description ? (
                                      <span>
                                        {conMidLeft.description.description}
                                      </span>
                                    ) : null
                                  }
                                  {...(conMidLeft.url
                                    ? { url: conMidLeft.url }
                                    : {})}
                                  titleClass={conMidLeft.cardTitle
                                    .toLowerCase()
                                    .replace(/[\s:,]+/g, "-")}
                                />
                              ) : null}
                              {conMidLeft.questionsAndAnswers ? (
                                <FAQ
                                  content={conMidLeft.questionsAndAnswers}
                                  data={data}
                                />
                              ) : null}
                            </div>
                          ))}
                      </Col>
                      <Col xs={12} md={3} key={gridItem.id}>
                        {/* ***************************** 4 Column - 25/25/25/25 - RIGHT COLUMN ***************************** */}
                        {Array.isArray(gridItem.middleRightColumn) &&
                          gridItem.middleRightColumn.map((conMidRight) => (
                            <div
                              id={
                                conMidRight.styleId ? conMidRight.styleId : null
                              }
                            >
                              {conMidRight.showName ? (
                                <h2>{conMidRight.name}</h2>
                              ) : null}
                              {conMidRight.body ? (
                                <p>
                                  {renderRichText(conMidRight.body, options)}
                                </p>
                              ) : null}
                              {conMidRight.image ? (
                                <img
                                  loading="lazy"
                                  alt={conMidRight.name}
                                  src={conMidRight.image.url}
                                />
                              ) : null}
                              {conMidRight.caption ? (
                                <caption>{conMidRight.caption}</caption>
                              ) : conMidRight.captionRichText ? (
                                <caption>
                                  {renderRichText(
                                    conMidRight.captionRichText,
                                    options
                                  )}
                                </caption>
                              ) : null}
                              {/* ********************************************** VIDEO ********************************************** */}
                              {conMidRight.videoName ? (
                                <ModalVideo
                                  key={conMidRight.id}
                                  title={conMidRight.videoName}
                                  youtubeID={conMidRight.videoId}
                                  caption={conMidRight.videoCaption}
                                  videoImageThumbnail={
                                    conMidRight.videoImageThumbnail
                                  }
                                />
                              ) : null}
                              {/* ********************************************** STATCARD ********************************************** */}
                              {conMidRight.cardTitle ? (
                                <StatCard
                                  key={conMidRight.id}
                                  icon={
                                    conMidRight.icon ? (
                                      <img
                                        loading="lazy"
                                        alt={conMidRight.cardTitle}
                                        src={conMidRight.icon.url}
                                        className="card-icons"
                                      />
                                    ) : null
                                  }
                                  title={conMidRight.cardTitle}
                                  subtitle={conMidRight.number}
                                  url={conMidRight.url}
                                  description={
                                    conMidRight.description &&
                                    conMidRight.description.description ? (
                                      <span>
                                        {conMidRight.description.description}
                                      </span>
                                    ) : null
                                  }
                                  {...(conMidRight.url
                                    ? { url: conMidRight.url }
                                    : {})}
                                  titleClass={conMidRight.cardTitle
                                    .toLowerCase()
                                    .replace(/[\s:,]+/g, "-")}
                                />
                              ) : null}
                              {conMidRight.questionsAndAnswers ? (
                                <FAQ
                                  content={conMidRight.questionsAndAnswers}
                                  data={data}
                                />
                              ) : null}
                            </div>
                          ))}
                      </Col>
                      <Col xs={12} md={3} key={gridItem.id}>
                        {/* ***************************** 4 Column - 25/25/25/25 - RIGHT COLUMN ***************************** */}
                        {Array.isArray(gridItem.rightColumn) &&
                          gridItem.rightColumn.map((conRight) => (
                            <div
                              id={conRight.styleId ? conRight.styleId : null}
                            >
                              {conRight.showName ? (
                                <h2>{conRight.name}</h2>
                              ) : null}
                              {conRight.body ? (
                                <p>{renderRichText(conRight.body)}</p>
                              ) : null}
                              {conRight.image ? (
                                <img
                                  loading="lazy"
                                  alt={conRight.name}
                                  src={conRight.image.url}
                                />
                              ) : null}
                              {conRight.caption ? (
                                <caption>{conRight.caption}</caption>
                              ) : conRight.captionRichText ? (
                                <caption>
                                  {renderRichText(
                                    conRight.captionRichText,
                                    options
                                  )}
                                </caption>
                              ) : null}
                              {/* ********************************************** VIDEO ********************************************** */}
                              {conRight.videoName ? (
                                <ModalVideo
                                  key={conRight.id}
                                  title={conRight.videoName}
                                  youtubeID={conRight.videoId}
                                  caption={conRight.videoCaption}
                                  videoImageThumbnail={
                                    conRight.videoImageThumbnail
                                  }
                                />
                              ) : null}
                              {/* ********************************************** STATCARD ********************************************** */}
                              {conRight.cardTitle ? (
                                <StatCard
                                  key={conRight.id}
                                  icon={
                                    conRight.icon ? (
                                      <img
                                        loading="lazy"
                                        alt={conRight.cardTitle}
                                        src={conRight.icon.url}
                                        className="card-icons"
                                      />
                                    ) : null
                                  }
                                  title={conRight.cardTitle}
                                  subtitle={conRight.number}
                                  url={conRight.url}
                                  description={
                                    conRight.description &&
                                    conRight.description.description ? (
                                      <span>
                                        {conRight.description.description}
                                      </span>
                                    ) : null
                                  }
                                  {...(conRight.url
                                    ? { url: conRight.url }
                                    : {})}
                                  titleClass={conRight.cardTitle
                                    .toLowerCase()
                                    .replace(/[\s:,]+/g, "-")}
                                />
                              ) : null}
                              {conRight.questionsAndAnswers ? (
                                <FAQ
                                  content={conRight.questionsAndAnswers}
                                  data={data}
                                />
                              ) : null}
                            </div>
                          ))}
                      </Col>
                    </Row>
                  </Container>
                </Container>
              </>
            );
          }
          return null;
        })}
      </Container>
    </Layout>
  );
};

export const Head = ({ data, pageContext }) => {
  const { nodes: pages } = data.allContentfulCrPage;
  const { navSlug } = pageContext;
  return (
    <>
      {pages.map((page) => {
        const comparesSlugz = pages.find((group) => page.slug === navSlug);
        if (comparesSlugz) {
          return (
            <Container key={page.title}>
              <Seo title={page.title} />
            </Container>
          );
        }
        return null;
      })}
    </>
  );
};

export default pageTemplate;

export const query = graphql`
  query GroupQuery {
    allContentfulCrContent {
      nodes {
        id
        name
        body {
          raw
        }
      }
    }
    allContentfulPdf {
      nodes {
        id
        contentful_id
        slug
        __typename
      }
    }
    allContentfulCrPage {
      nodes {
        id
        contentful_id
        slug
        title
        __typename
        content {
          ... on ContentfulGridTwoColumn {
            id
            layout
            name
            showTitle
            leftColumn {
              ... on ContentfulCrContent {
                id
                styleId
                name
                showName
                body {
                  raw
                }
              }
              ... on ContentfulCard {
                id
                styleId
                cardTitle
                number
                url
                icon {
                  url
                }
                description {
                  description
                }
              }
              ... on ContentfulBlockImage {
                name
                styleId
                showName
                caption
                captionRichText {
                  raw
                }
                image {
                  url
                }
              }
              ... on ContentfulFaq {
                styleId
                questionsAndAnswers {
                  id
                  styleId
                  faq {
                    title
                    showName
                  }
                  question
                  answer {
                    raw
                  }
                }
              }

              ... on ContentfulModalVideo {
                id
                styleId
                videoName
                videoId
                videoCaption {
                  raw
                }
                videoImageThumbnail {
                  gatsbyImageData(width: 500, layout: CONSTRAINED)
                }
              }
              ... on ContentfulPdf {
                slug
                documentTitle
                id
                contentful_id
                pdfDocument {
                  title
                  description
                }
              }
              ... on ContentfulCarousel {
                id
                styleId
                carouselTitle
                carouselSlideContainer {
                  id
                  slideImage {
                    id
                    description
                    file {
                      fileName
                      url
                      details {
                        image {
                          width
                          height
                        }
                      }
                    }
                  }
                  slideLabel
                  slideDescription {
                    id
                    slideDescription
                  }
                  id
                  slideImage {
                    id
                    description
                    file {
                      details {
                        image {
                          width
                          height
                        }
                      }
                      fileName
                      url
                    }
                  }
                  slideLabel
                  slideDescription {
                    id
                    slideDescription
                  }
                }
              }
            }
            rightColumn {
              ... on ContentfulCrContent {
                id
                styleId
                name
                showName
                body {
                  raw
                }
              }
              ... on ContentfulCard {
                id
                styleId
                cardTitle
                number
                url
                icon {
                  url
                }
                description {
                  description
                }
              }
              ... on ContentfulBlockImage {
                name
                styleId
                showName
                caption
                captionRichText {
                  raw
                }
                image {
                  url
                }
              }
              ... on ContentfulFaq {
                styleId
                questionsAndAnswers {
                  id
                  styleId
                  faq {
                    title
                    showName
                  }
                  question
                  answer {
                    raw
                  }
                }
              }
              ... on ContentfulModalVideo {
                id
                styleId
                videoName
                videoId
                videoCaption {
                  raw
                }
                videoImageThumbnail {
                  gatsbyImageData(width: 500, layout: CONSTRAINED)
                }
              }
              ... on ContentfulPdf {
                slug
                documentTitle
                id
                contentful_id
                pdfDocument {
                  title
                  description
                }
              }
              ... on ContentfulCarousel {
                id
                styleId
                carouselTitle
                carouselSlideContainer {
                  id
                  slideImage {
                    id
                    description
                    file {
                      fileName
                      url
                      details {
                        image {
                          width
                          height
                        }
                      }
                    }
                  }
                  slideLabel
                  slideDescription {
                    id
                    slideDescription
                  }
                  id
                  slideImage {
                    id
                    description
                    file {
                      details {
                        image {
                          width
                          height
                        }
                      }
                      fileName
                      url
                    }
                  }
                  slideLabel
                  slideDescription {
                    id
                    slideDescription
                  }
                }
              }
            }
          }
          ... on ContentfulGridThreeColumn {
            id
            layout
            name
            showTitle
            leftColumn {
              ... on ContentfulCrContent {
                name
                styleId
                showName
                body {
                  raw
                }
              }
              ... on ContentfulBlockImage {
                name
                styleId
                showName
                caption
                captionRichText {
                  raw
                }
                image {
                  url
                }
              }
              ... on ContentfulModalVideo {
                id
                styleId
                videoName
                videoId
                videoCaption {
                  raw
                }
                videoImageThumbnail {
                  gatsbyImageData(width: 500, layout: CONSTRAINED)
                }
              }
              ... on ContentfulCard {
                id
                styleId
                cardTitle
                number
                url
                icon {
                  url
                }
                description {
                  description
                }
              }
              ... on ContentfulFaq {
                styleId
                questionsAndAnswers {
                  id
                  styleId
                  faq {
                    title
                    showName
                  }
                  question
                  answer {
                    raw
                  }
                }
              }
            }
            middleColumn {
              ... on ContentfulCrContent {
                name
                styleId
                showName
                body {
                  raw
                }
              }
              ... on ContentfulBlockImage {
                name
                styleId
                showName
                caption
                captionRichText {
                  raw
                }
                image {
                  url
                }
              }
              ... on ContentfulModalVideo {
                id
                styleId
                videoName
                videoId
                videoCaption {
                  raw
                }
                videoImageThumbnail {
                  gatsbyImageData(width: 500, layout: CONSTRAINED)
                }
              }
              ... on ContentfulCard {
                id
                styleId
                cardTitle
                number
                url
                icon {
                  url
                }
                description {
                  description
                }
              }
              ... on ContentfulFaq {
                styleId
                questionsAndAnswers {
                  id
                  styleId
                  faq {
                    title
                    showName
                  }
                  question
                  answer {
                    raw
                  }
                }
              }
            }
            rightColumn {
              ... on ContentfulCrContent {
                name
                styleId
                showName
                body {
                  raw
                }
              }
              ... on ContentfulBlockImage {
                name
                styleId
                showName
                caption
                captionRichText {
                  raw
                }
                image {
                  url
                }
              }
              ... on ContentfulModalVideo {
                id
                styleId
                videoName
                videoId
                videoCaption {
                  raw
                }
                videoImageThumbnail {
                  gatsbyImageData(width: 500, layout: CONSTRAINED)
                }
              }
              ... on ContentfulCard {
                id
                styleId
                cardTitle
                number
                url
                icon {
                  url
                }
                description {
                  description
                }
              }
              ... on ContentfulFaq {
                styleId
                questionsAndAnswers {
                  id
                  styleId
                  faq {
                    title
                    showName
                  }
                  question
                  answer {
                    raw
                  }
                }
              }
            }
          }
          ... on ContentfulGridFourColumn {
            id
            layout
            name
            showTitle
            leftColumn {
              ... on ContentfulCrContent {
                id
                styleId
                name
                showName
                body {
                  raw
                }
              }
              ... on ContentfulCard {
                id
                styleId
                cardTitle
                number
                url
                description {
                  description
                }
              }
              ... on ContentfulFaq {
                styleId
                questionsAndAnswers {
                  id
                  styleId
                  faq {
                    title
                    showName
                  }
                  question
                  answer {
                    raw
                  }
                }
              }
              ... on ContentfulModalVideo {
                id
                styleId
                videoName
                videoId
                videoCaption {
                  raw
                }
                videoImageThumbnail {
                  gatsbyImageData(width: 500, layout: CONSTRAINED)
                }
              }
            }
            middleLeftColumn {
              ... on ContentfulCrContent {
                id
                styleId
                name
                showName
                body {
                  raw
                }
              }
              ... on ContentfulCard {
                id
                styleId
                cardTitle
                number
                url
                icon {
                  url
                }
                description {
                  description
                }
              }
              ... on ContentfulFaq {
                styleId
                questionsAndAnswers {
                  id
                  styleId
                  faq {
                    title
                    showName
                  }
                  question
                  answer {
                    raw
                  }
                }
              }
              ... on ContentfulModalVideo {
                id
                styleId
                videoName
                videoId
                videoCaption {
                  raw
                }
                videoImageThumbnail {
                  gatsbyImageData(width: 500, layout: CONSTRAINED)
                }
              }
            }
            middleRightColumn {
              ... on ContentfulCrContent {
                id
                styleId
                name
                showName
                body {
                  raw
                }
              }
              ... on ContentfulCard {
                id
                styleId
                cardTitle
                number
                url
                description {
                  description
                }
              }
              ... on ContentfulFaq {
                styleId
                questionsAndAnswers {
                  id
                  styleId
                  faq {
                    title
                    showName
                  }
                  question
                  answer {
                    raw
                  }
                }
              }
              ... on ContentfulModalVideo {
                id
                styleId
                videoName
                videoId
                videoCaption {
                  raw
                }
                videoImageThumbnail {
                  gatsbyImageData(width: 500, layout: CONSTRAINED)
                }
              }
            }
            rightColumn {
              ... on ContentfulCrContent {
                id
                styleId
                name
                showName
                body {
                  raw
                }
              }
              ... on ContentfulCard {
                id
                styleId
                cardTitle
                number
                url
                icon {
                  url
                }
                description {
                  description
                }
              }
              ... on ContentfulFaq {
                styleId
                questionsAndAnswers {
                  id
                  styleId
                  faq {
                    title
                    showName
                  }
                  question
                  answer {
                    raw
                  }
                }
              }
              ... on ContentfulModalVideo {
                id
                styleId
                videoName
                videoId
                videoCaption {
                  raw
                }
                videoImageThumbnail {
                  gatsbyImageData(width: 500, layout: CONSTRAINED)
                }
              }
            }
          }
          ... on ContentfulGridOneColumn {
            id
            layout
            name
            showTitle
            singleColumn {
              ... on ContentfulCrContent {
                id
                styleId
                name
                showName
                body {
                  raw
                }
              }
              ... on ContentfulBlockImage {
                name
                styleId
                showName
                caption
                captionRichText {
                  raw
                }
                image {
                  url
                }
              }
              ... on ContentfulCard {
                id
                styleId
                cardTitle
                number
                url
                icon {
                  url
                }
                description {
                  description
                }
              }
              ... on ContentfulFaq {
                styleId
                questionsAndAnswers {
                  id
                  styleId
                  faq {
                    title
                    showName
                  }
                  question
                  answer {
                    raw
                  }
                }
              }
              ... on ContentfulPdf {
                slug
                documentTitle
                id
                contentful_id
                pdfDocument {
                  title
                  description
                }
              }
              ... on ContentfulModalVideo {
                id
                styleId
                videoName
                videoId
                videoCaption {
                  raw
                }
                videoImageThumbnail {
                  gatsbyImageData(width: 500, layout: CONSTRAINED)
                }
              }
              ... on ContentfulCarousel {
                id
                styleId
                carouselTitle
                carouselSlideContainer {
                  id
                  slideImage {
                    id
                    description
                    file {
                      fileName
                      url
                      details {
                        image {
                          width
                          height
                        }
                      }
                    }
                  }
                  slideLabel
                  slideDescription {
                    id
                    slideDescription
                  }
                  id
                  slideImage {
                    id
                    description
                    file {
                      details {
                        image {
                          width
                          height
                        }
                      }
                      fileName
                      url
                    }
                  }
                  slideLabel
                  slideDescription {
                    id
                    slideDescription
                  }
                }
              }
            }
          }
        }
      }
    }
    allContentfulCrNavItem {
      nodes {
        navSlug
        navItemName
        navNextLevel {
          navSlug
          navNextLevel {
            navSlug
            navNextLevel {
              navSlug
            }
          }
        }
      }
    }
    allContentfulCrNavGroup {
      nodes {
        navGroupName
        navUrl
        navSlug
        contentful_id
        navNextLevel {
          navSlug
          navItemName
          contentful_id
          navNextLevel {
            navSlug
            contentful_id
            navNextLevel {
              navSlug
              contentful_id
            }
          }
        }
      }
    }
  }
`;
